import {Box, ButtonBase, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {Dates, isSameDate} from '../../../common'
import {customThemeConstants} from '../../theme'

import {QuickSelectionItem} from './types'

interface QuickSelectionProps {
  handleItemClick: (dates: Dates<Date>, item: QuickSelectionItem) => void
  items?: QuickSelectionItem[]
  selectedItem?: QuickSelectionItem
  dateCustomTemplate?: (option: QuickSelectionItem, language?: string) => string
}

const defaultCustomTemplate = (item: QuickSelectionItem, language: string) => {
  return isSameDate(item.dates[0], item.dates[1])
    ? item.dates[0]?.toLocaleDateString(language)
    : item.dates[0] &&
        item.dates[1] &&
        `${item.dates[0].toLocaleDateString(language)} - ${item.dates[1].toLocaleDateString(
          language
        )}`
}

export const QuickSelection: React.FC<QuickSelectionProps> = ({
  handleItemClick,
  items,
  selectedItem,
  dateCustomTemplate
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const today = new Date()
  const oneWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7)
  const previousWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
  const oneMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())
  const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
  const onSelectionItemClick = (item: QuickSelectionItem) => {
    handleItemClick({startDate: item.dates[0], endDate: item.dates[1] || null}, item)
  }

  const selectionItems: QuickSelectionItem[] = items || [
    {
      title: t('datePicker.todaySelection'),
      dates: [today, today]
    },
    {
      title: t('datePicker.weekSelection'),
      dates: [today, oneWeek]
    },
    {
      title: t('datePicker.monthSelection'),
      dates: [today, oneMonth]
    },
    {
      title: t('datePicker.previousWeekSelection'),
      dates: [previousWeek, today]
    },
    {
      title: t('datePicker.previousMonthSelection'),
      dates: [previousMonth, today]
    }
  ]

  const customOptionTemplate = dateCustomTemplate || defaultCustomTemplate

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
      {selectionItems.map((item: QuickSelectionItem, i: number) => {
        const isSelected =
          Boolean(item.id) && (selectedItem?.id === item.id || selectedItem?.title === item.title)
        return (
          <ButtonBase
            data-test-id={`quick-selection-button-${i}`}
            key={i}
            sx={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              padding: 1.5,
              '&:hover, &:focus': {
                backgroundColor: customThemeConstants().palette.contentHover
              },
              ...(isSelected && {
                backgroundColor: customThemeConstants().palette.contentSelected,
                '&:hover, &:focus': {
                  backgroundColor: customThemeConstants().palette.contentSelectedHover
                }
              }),
              ':not(:last-child)': {
                borderBottom: '1px solid',
                borderColor: ({palette}) => palette.divider
              }
            }}
            data-state={isSelected ? 'selected' : 'unselected'}
            aria-label={`Date Picker ${item.title} button`}
            onClick={() => onSelectionItemClick(item)}
          >
            <Typography component="span" color="textPrimary">
              {item.title}
            </Typography>
            <Typography component="span" variant="caption">
              {customOptionTemplate(item, language)}
            </Typography>
          </ButtonBase>
        )
      })}
    </Box>
  )
}
